import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Grid, Group, Progress, Stack, Tabs, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Cpu,
  ExternalLink,
  Network,
  PauseCircle,
  Play,
  Power,
  ServerCog,
  Snowflake,
  Terminal,
} from 'lucide-react';
import { Suspense, lazy, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  ErrorState,
  Mono,
  PageContainer,
  PageHeader,
  Section,
  Skeleton,
  StatusDot,
  Table,
  type TableColumn,
} from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { AttackPhaseTimeline, type PhaseTimelineItem } from '../components/range/AttackPhaseTimeline';
import { timelineToPhaseItems } from '../components/range/timelineMap';
import { ProvisioningPanel } from '../components/range/ProvisioningPanel';
import {
  useDestroyRange,
  useHibernateRange,
  useRange,
  useResumeRange,
  useStartPhase,
} from '../hooks/useRanges';
import { useAuthStore } from '../store/useAuthStore';
import { useRangeHealth } from '../hooks/useRangeHealth';
import { useRangeTimeline } from '../hooks/useTimeline';
import type { RangeHealthVm, RangeWarning } from '../api/types';
import { colors } from '../theme/tokens';

const CommandCenterView = lazy(() => import('../components/range/CommandCenterView'));

export const Route = createFileRoute('/ranges/$id')({
  component: RangeDetail,
});

export function RangeDetail() {
  const { id } = Route.useParams();
  const { data, isPending, isError, error } = useRange(id);
  const healthQ = useRangeHealth(id);
  const destroy = useDestroyRange();
  const hibernate = useHibernateRange();
  const resume = useResumeRange();
  const startPhase = useStartPhase();
  const role = useAuthStore((s) => s.user?.role);
  const canAttack = role === 'instructor' || role === 'org_admin' || role === 'superadmin';
  const [busyPhase, setBusyPhase] = useState<string | undefined>(undefined);
  const runPhase = (phaseId: string) => {
    setBusyPhase(phaseId);
    startPhase.mutate(
      { rangeId: id, phaseId },
      { onSettled: () => setBusyPhase(undefined) },
    );
  };
  const navigate = useNavigate();
  const [confirmDestroy, setConfirmDestroy] = useState(false);
  const openConsole = (vmName: string) =>
    navigate({ to: '/console/$rangeId/$vmName', params: { rangeId: id, vmName } });
  // Pull the actual VM list from the monitor endpoint — useRange's
  // RangeDetail.vms type is a stale RangeVm shape that doesn't match what
  // /monitor/ranges/{id}/health actually returns. Real data lives in
  // healthQ.data.vms (RangeHealthVm shape with vmName, state, ssmStatus).
  const liveVms: ReadonlyArray<RangeHealthVm> = healthQ.data?.vms ?? [];

  // Deploy-time AMI-staleness warnings (services/shared/domain/ami_freshness.py),
  // echoed by the health endpoint from the range registry row. NON-BLOCKING —
  // the range works; the banner exists so a missed re-bake (most sharply a
  // lapsed Splunk Enterprise trial degrading to Splunk Free) is caught before
  // an exercise rather than mid-exercise.
  const warnings: ReadonlyArray<RangeWarning> = healthQ.data?.warnings ?? [];

  // Attack-phase timeline: real kill-chain data from the score service, which
  // correlates phase-start telemetry against `attack.detected` events and polls
  // ~10s so detect-latency stays live. Mapped to the presentational shape so the
  // stepper renders "detected · MTTD" / "undetected · coverage gap". Empty until
  // the first phase fires — the empty-state below covers that.
  const timelineQ = useRangeTimeline(id);
  const phases: ReadonlyArray<PhaseTimelineItem> = useMemo(
    () => timelineToPhaseItems(timelineQ.data?.timeline ?? []),
    [timelineQ.data],
  );

  if (isPending) {
    return (
      <PageContainer size="wide">
        <Stack gap="lg">
          <Skeleton height={48} width="40%" />
          <Skeleton height={120} radius="md" />
          <Skeleton height={520} radius="md" />
        </Stack>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer size="wide">
        <ErrorState title="Could not load range" error={error} />
      </PageContainer>
    );
  }

  const vmColumns: ReadonlyArray<TableColumn<RangeHealthVm>> = [
    {
      key: 'vmName',
      header: 'Host',
      cell: (vm) => (
        <Group gap={6} wrap="nowrap">
          <StatusDot
            state={vm.state === 'running' ? 'running' : vm.state === 'pending' ? 'idle' : 'error'}
            size="xs"
          />
          <Text size="sm" className="font-mono" c="rgb(var(--c-txt-bright))">
            {vm.vmName || vm.hostname}
          </Text>
        </Group>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      cell: (vm) => <Text size="sm" c="dimmed">{vm.role}</Text>,
    },
    {
      key: 'state',
      header: 'State',
      cell: (vm) => (
        <Badge variant={vm.state === 'running' ? 'success' : 'warn'} size="xs">{vm.state}</Badge>
      ),
    },
    {
      key: 'ip',
      header: 'IP',
      cell: (vm) => (
        <Text size="sm" className="font-mono" c="dimmed">{vm.privateIp ?? '—'}</Text>
      ),
    },
    {
      key: 'ssm',
      header: 'SSM',
      cell: (vm) => (
        <Badge variant={vm.ssmStatus === 'Online' ? 'success' : 'warn'} size="xs">
          {vm.ssmStatus}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (vm) => (
        <Button
          variant="ghost"
          size="xs"
          leftIcon={<Terminal size={12} />}
          onClick={() => openConsole(vm.vmName)}
          disabled={!vm.healthy}
        >
          Console
        </Button>
      ),
    },
  ];

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={ServerCog}
        title={
          <Group gap="sm" align="center" wrap="nowrap">
            <span>{data.scenarioName || id}</span>
            <Badge variant={data.status === 'attacking' ? 'warn' : 'success'}>{data.status}</Badge>
          </Group>
        }
        description="Live status, topology, phases and SOC for this range."
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Ranges', to: '/ranges' },
          { label: 'Detail' },
        ]}
        actions={
          <>
            {/* F8: near-term features — disabled with a tooltip rather than a
                live-looking control that silently does nothing mid-session. */}
            <Button variant="ghost" leftIcon={<PauseCircle size={14} />} disabled title="Coming soon">
              Pause attack
            </Button>
            {/* DS-9: hibernate stops the range's compute between sessions;
                Resume starts it back in ~1-2 min. */}
            {data.status === 'hibernated' ? (
              <Button
                variant="secondary"
                leftIcon={<Play size={14} />}
                loading={resume.isPending}
                onClick={() => resume.mutate({ rangeId: id })}
              >
                Resume
              </Button>
            ) : (
              <Button
                variant="ghost"
                leftIcon={<Snowflake size={14} />}
                loading={hibernate.isPending}
                onClick={() => hibernate.mutate({ rangeId: id })}
              >
                Hibernate
              </Button>
            )}
            <Button variant="danger" leftIcon={<Power size={14} />} onClick={() => setConfirmDestroy(true)}>
              Destroy
            </Button>
          </>
        }
      />

      <Text c="dimmed" size="sm" mb="md" className="font-mono">
        {id} · {data.vmCount} VMs · phase{' '}
        <span style={{ color: colors.hudCyan }}>{data.currentPhase ?? 'idle'}</span>
      </Text>

      {warnings.length > 0 ? (
        <Card variant="warning" mb="md">
          <Stack gap="xs">
            {warnings.map((w) => (
              <Group key={`${w.code}:${w.role}`} gap="sm" align="flex-start" wrap="nowrap">
                <AlertTriangle
                  size={16}
                  style={{ flexShrink: 0, marginTop: 2 }}
                  color={w.severity === 'critical' ? colors.hudRed : colors.hudAmber}
                />
                <Stack gap={4}>
                  <Group gap="xs">
                    <Badge variant={w.severity === 'critical' ? 'danger' : 'warn'} size="xs">
                      {w.role}
                    </Badge>
                    <Text size="sm">{w.message}</Text>
                  </Group>
                  {w.action ? (
                    <Text size="xs" c="dimmed">
                      Fix: <Mono>{w.action}</Mono>
                    </Text>
                  ) : null}
                </Stack>
              </Group>
            ))}
          </Stack>
        </Card>
      ) : null}

      <Section title="Run status">
      <motion.div variants={staggerContainer} initial="hidden" animate="show">
      {/* F2: during the ~10-min wait, show the live provisioning stepper (fed by
          the per-VM health poll) instead of the attack-progress card, which is
          bound to attack progress and reads 0% until an attack is triggered. */}
      {(data.status === 'pending' || data.status === 'provisioning') ? (
        <motion.div variants={cardItem}>
          <ProvisioningPanel
            status={data.status}
            vms={liveVms}
            vmCount={data.vmCount}
            startedAt={data.startedAt ?? null}
          />
        </motion.div>
      ) : (
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <motion.div variants={cardItem}>
          <Card>
            <Stack gap="md">
              <Group justify="space-between">
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Wall-clock progress
                </Text>
                <Text size="xs" c={colors.neon} className="font-mono">
                  {Math.round((data.progress ?? 0) * 100)}%
                </Text>
              </Group>
              <Progress value={(data.progress ?? 0) * 100} color="neon" radius="xl" size="md" />
              <Group gap="lg">
                <Stat
                  label="Detections"
                  value={String(data.detectionsFired ?? 0)}
                  color={colors.hudAmber}
                />
                <Stat
                  label="Objectives"
                  value={`${data.objectivesCompleted ?? 0} / ${data.objectivesTotal ?? 0}`}
                  color={colors.neon}
                />
                <Stat
                  label="Cost so far"
                  value={`$${(data.costSoFar ?? 0).toFixed(2)}`}
                  color={colors.hudCyan}
                />
              </Group>
            </Stack>
          </Card>
          </motion.div>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <motion.div variants={cardItem}>
          <Card>
            <Stack gap="sm">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Attack phases
              </Text>
              {phases.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No phase data yet. Caldera operation telemetry pipes in when
                  the attack starts.
                </Text>
              ) : (
                <AttackPhaseTimeline
                  phases={phases}
                  onStart={canAttack ? runPhase : undefined}
                  busyPhaseId={busyPhase}
                />
              )}
            </Stack>
          </Card>
          </motion.div>
        </Grid.Col>
      </Grid>
      )}
      </motion.div>
      </Section>

      <Section title="Infrastructure" description="Network topology, virtual machines and remote consoles for this range.">
      <Tabs defaultValue="topology" variant="default">
        <Tabs.List>
          <Tabs.Tab value="topology" leftSection={<Network size={14} />}>
            Topology
          </Tabs.Tab>
          <Tabs.Tab value="vms" leftSection={<Cpu size={14} />}>
            VMs
          </Tabs.Tab>
          <Tabs.Tab value="consoles" leftSection={<ExternalLink size={14} />}>
            Consoles
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="topology" pt="md">
          <Suspense fallback={<Skeleton height={520} radius="md" />}>
            <CommandCenterView vms={liveVms} onConsole={openConsole} rangeId={id} />
          </Suspense>
        </Tabs.Panel>
        <Tabs.Panel value="vms" pt="md">
          <Card>
            <Table columns={vmColumns} rows={liveVms} rowKey={(vm) => vm.instanceId} />
          </Card>
        </Tabs.Panel>
        <Tabs.Panel value="consoles" pt="md">
          <Card>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Guacamole is the primary remote-console layer. Tokens are minted JIT against the
                user's Cognito session and expire after 15 minutes. SSM Session Manager is
                available as a fallback for headless servers.
              </Text>
              <Group gap="sm" mt="md">
                {liveVms
                  .filter((vm) => vm.healthy)
                  .map((vm) => (
                    <Button
                      key={vm.instanceId}
                      variant="secondary"
                      size="xs"
                      leftIcon={<Terminal size={12} />}
                      onClick={() => openConsole(vm.vmName)}
                    >
                      {vm.vmName || vm.hostname}
                    </Button>
                  ))}
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
      </Section>

      {/* Typed confirmation: destroy is irreversible and tears down every VM,
          so a bare Yes/No was too easy to fire by accident. NOTE: the previous
          copy promised "snapshots are taken automatically before destroy" —
          services/deploy/handler.py does no such thing, so that sentence was
          removed rather than left as a dangerous false reassurance. */}
      <ConfirmDialog
        opened={confirmDestroy}
        onClose={() => setConfirmDestroy(false)}
        title="Destroy this range?"
        confirmLabel="Destroy range"
        confirmText={id}
        loading={destroy.isPending}
        onConfirm={() =>
          destroy.mutate(
            { rangeId: id },
            {
              // On destroy: close the dialog and return the operator to
              // "My Ranges" with a "range destroyed" confirmation banner —
              // this detail page is about to go stale.
              onSuccess: () => {
                setConfirmDestroy(false);
                navigate({ to: '/ranges', search: { destroyed: id } });
              },
            },
          )
        }
      >
        This removes every VM in this range and cannot be undone. There is no automatic
        snapshot — anything not exported is lost.
      </ConfirmDialog>
    </PageContainer>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text className="font-mono" fw={700} size="20px" style={{ color }}>
        {value}
      </Text>
    </Stack>
  );
}
